export const SelectedNavbar = {
  // FA204 / frank trap — isDsKey/dsLabel/navTitle used to be bare
  // module-scope const read from inside `onDblclick`/`text` factories; they
  // read as undefined once this component is frank.toJSON'd for platform
  // delivery (workspace d6ea1142, project_frank_drops_module_local_helpers).
  // Moved into `scope`; navTitle calls its siblings via `this.<name>`
  // (object-literal properties can't see each other as bare identifiers).
  scope: {
    // Built-in design-system canvas frames are keyed `__ds/<route>` (canvas/
    // functions/canvas/state.js). They're read-only reference views, so the
    // navbar shows a friendly label and skips rename. (2026-06-17.)
    isDsKey(k) {
      return typeof k === 'string' && k.indexOf('__ds/') === 0
    },
    // `el` is threaded through because `el.scope.navTitle(...)` binds `this`
    // to the SCOPE object, not the element — polyglot needs the element.
    dsLabel(el, k) {
      return el.call('polyglot', 'editor.selectedNavbar.dsTitle').replace(
        '{route}',
        k.slice(5).replace(/^\w/, (c) => c.toUpperCase())
      )
    },
    navTitle(el, k) {
      return this.isDsKey(k) ? this.dsLabel(el, k) : k
    }
  },
  // if: (el) => el.getUserSettings('allowTitleOnArtboard'),
  state: {
    type: 'components',
    value: {},
    isEditMode: false
  },
  extends: 'EditNavbar',
  minWidth: 'G1',
  position: 'absolute',
  bottom: '100%',
  margin: 'Z -',
  left: '50%',
  transition: 'X opacity defaultBezier',
  padding: '0 W 0 Z2',
  round: 'radiusCard',
  top: 'auto',
  pointerEvents: 'auto',
  theme: 'common-box',
  // Card-outline on a floating island over live canvas content
  // (design-direction.md §8) — the prescribed separation for a floating
  // toolbar is elevation (DESIGN_LANGUAGE §2.3 `float`), not a stroke; same
  // fix as MainCanvasNavbar.js / SelectedLayerNavbar.js.
  shadow: 'float',
  width: 'calc(100% + Y1 * 2)',
  maxWidth: 'calc(100% + Y1 * 2)',
  fontSize: 'Z1',
  isEditing: (el, s) => s.isEditMode,
  transform: 'translate3d(-50%, 0, 1px)',
  '.isEditing': {
    theme: 'field'
  },
  '!isEditing': {
    theme: 'navbar'
  },
  onBeforeUpdate: (_, el, s, ctx, opts) => {
    s.isEditMode = Boolean(opts.force)

    const selected = el.getSelectedKey()
    if (!selected) return
    s.key = selected
    s.type = selected.startsWith('/') ? 'pages' : 'components'
  },
  EditorPositionNavbar: null,
  NavbarButtonSet_meta: null,
  NavbarButtonSet_title: {
    maxWidth: '40%',
    hide: (el, s) => s.isEditMode,
    onDblclick: (ev, el, s) => {
      // Built-in design-system frames can't be renamed — skip edit mode.
      if (el.scope.isDsKey(el.getSelectedKey())) return
      // v2 boards: the rename pipeline behind this form (renameLegacy —
      // shared/functions/rename.js) is the v1 Form/schema flow and does not
      // rename v2 project pages; entering edit mode would offer a form whose
      // Save can't work. Skip until a v2 rename lands (tickets/workspace.md).
      if (el.call('isCanvasV2')) return
      s.update(
        {
          isEditMode: true
        },
        {
          force: true
        }
      )
    },
    Text: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '1.7',
      ignoreChildExtends: true,
      text: (el) => el.scope.navTitle(el, el.getSelectedKey())
    },
    CopyButton: {
      // Brand CopyButton (extends IconButton) only sets `fontSize: 'Z'` —
      // Icon ignores fontSize for its own box, so that alone doesn't pin a
      // size. boxSize rides the SPACING scale (not the fontSize scale): 'Z'
      // rendered a 7px glyph beside the row's 12px icons — too small to read
      // as a control (2026-08-08 per Nika "icons sizes"). Z2 keeps it one
      // step under the row, clearly secondary but legible.
      Icon: { boxSize: 'Z2 Z2' },
      value: (el, s) => el.getSelectedKey()
    }
  },
  EditorTools: {
    margin: '- - - auto',
    hide: (el, s) => s.isEditMode,
    gap: 'U',
    paddingInlineEnd: '0',
    // Icon's rendered box comes from width/height (boxSize), NEVER
    // fontSize — every button below used to leave it unset and silently
    // ride SquareButton's ambient inherited font-size, which is how this
    // row could drift out of proportion with itself. Pin one explicit
    // boxSize across the row instead (tickets/design "selected navbar
    // icon misproportions").
    PreviewBtn: {
      icon: 'sf play',
      title: (el) => el.call('polyglot', 'editor.selectedNavbar.preview'),
      Icon: { boxSize: 'A A' },
      onClick: (ev, el, s) => {
        // s.key seeds in onBeforeUpdate, which may not have run before the
        // first click — fall back to the live selection.
        el.activateChosen(s.key || el.getSelectedKey())
        el.call('clearFollowing')
      }
    },
    ContextBtn: {
      icon: 'three dots vertical',
      title: (el) => el.call('polyglot', 'editor.layerNavbar.options'),
      Icon: { boxSize: 'A A' },
      onClick: (ev, el, s) => {
        el.activateContext(ev, s.key)
      },
      order: 3
    },
    childExtends: 'NavbarButton',
    Refresh: {
      icon: 'reload outline',
      title: (el) => el.call('polyglot', 'common.action.refresh'),
      Icon: { boxSize: 'A A' },
      onClick: (ev, el) => {
        const key = el.getSelectedKey()
        const item = el.getItem?.(key)
        el.call('refreshComponentOnCanvas', key, item)
      }
    },
    InNewTab: {
      icon: 'arrow up right',
      title: (el) => el.call('polyglot', 'members.sharingNavbar.newTab'),
      Icon: { boxSize: 'A A' },
      onClick: (ev, el) => {
        const url = el.call('generatePreviewUrl')
        window.open(url, 'Symbols preview')
      }
    },
    TextNavbarButtonDropdown: {}
  },
  FileSidebarAddNewItem: {
    hide: (el, s) => !s.isEditMode,
    flex: 1,
    padding: '- Y1 - -',
    onSubmit: async (ev, el, s) => {
      ev.preventDefault()
      const capitalizeType = el.call('capitalize', s.type)
      await el.call(`rename${capitalizeType}`, 'new-key', async (newKey) => {
        el.node.reset()
        s.update({
          isEditMode: false,
          key: newKey
        })
      })
    },
    Label: {
      border: 'none',
      '.hasItems': null,
      margin: '0',
      Icon: null,
      Input: {
        autofocus: true,
        padding: '0',
        value: (el, s) => {
          return el.getSelectedKey()
        },
        onInput: () => {},
        attr: {
          required: true,
          placeholder: (el, s) =>
            el.call(
              'polyglot',
              s.type === 'pages'
                ? 'editor.selectedNavbar.pathPlaceholder'
                : 'editor.selectedNavbar.componentPlaceholder'
            )
        }
      },
      Flex: {
        childExtends: 'NavbarButton',
        Save: {
          padding: 'Y2',
          fontSize: 'A'
        },
        Close: {
          order: 2,
          padding: 'Y2',
          fontSize: 'A',
          onClick: (ev, el, s) => {
            s.parent.update({
              isEditMode: false
            })
          }
        }
      }
    }
  }
}
